/** English strings for the registry area. The Vietnamese file must have exactly the same keys. */
export const registry = {
  area: 'Registry Ops',
  any: 'Any',
  none: 'none',
  unlimited: 'unlimited',
  yes: 'Yes',
  no: 'No',
  refresh: 'Refresh',
  resetFilters: 'Reset filters',
  newest: 'Newest',
  older: 'Older',
  units: { ms: '{{n}} ms', s: '{{n}} s', min: '{{n}} min' },
  filter: {
    registry: 'Registry',
    status: 'Status',
    outcome: 'Outcome',
    object: 'Object',
    trafficClass: 'Traffic class',
    command: 'Command',
    commandPlaceholder: 'e.g. domain_renew',
    objectKey: 'Object key',
    objectKeyPlaceholder: 'exact name / handle',
    trid: 'TRID',
    tridPlaceholder: 'clTRID or svTRID',
    state: 'State',
    type: 'Type',
    enterHint: 'Press Enter to apply',
  },
  enum: {
    registryStatus: { ACTIVE: 'Active', SUSPENDED: 'Suspended', RETIRED: 'Retired' },
    credentialStatus: { PENDING: 'Pending', ACTIVE: 'Active', ROTATED: 'Rotated', REVOKED: 'Revoked' },
    issueState: { OPEN: 'Open', AUTO_RECONCILING: 'Auto-reconciling', MANUAL_REVIEW: 'Manual review', RESOLVED: 'Resolved', IGNORED: 'Ignored' },
    severity: { LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High', CRITICAL: 'Critical' },
    commandState: {
      PENDING: 'Pending',
      SENT: 'Sent',
      SUCCESS: 'Success',
      REJECTED: 'Rejected',
      UNKNOWN: 'Unknown',
      RECONCILING: 'Reconciling',
      RETRY_PENDING: 'Retry pending',
      MANUAL_REVIEW: 'Manual review',
      UNRESOLVED: 'Not known yet (unknown / reconciling / manual review)',
    },
    objectType: { DOMAIN: 'Domain', CONTACT: 'Contact', HOST: 'Host', SESSION: 'Session', POLL: 'Poll' },
    trafficClass: { INTERACTIVE: 'Interactive', BACKGROUND: 'Background', POLL: 'Poll', DROPCATCH: 'Drop-catch' },
    pollStatus: { UNPROCESSED: 'Not processed', UNACKED: 'Not acknowledged', ERROR: 'Processing error', PROCESSED: 'Processed' },
    issueType: {
      EXISTENCE: 'Existence',
      SPONSORSHIP: 'Sponsorship',
      EXPIRY: 'Expiry',
      STATUS: 'Status',
      CONTACT: 'Contact',
      NAMESERVER: 'Nameserver',
      DNSSEC: 'DNSSEC',
      TRANSFER: 'Transfer',
      UNKNOWN_OUTCOME: 'Unknown outcome',
    },
    tldClass: { VN: '.vn', GTLD: 'gTLD', NEW_GTLD: 'New gTLD', CCTLD: 'ccTLD', OTHER: 'Other' },
    dossierType: {
      VN_DOMAIN: '.vn dossier',
      INTERNATIONAL_GTLD: 'International gTLD dossier',
      INTERNATIONAL_NEW_GTLD: 'International new gTLD dossier',
      INTERNATIONAL_CCTLD: 'International ccTLD dossier',
      OTHER: 'Other dossier',
    },
  },
  unknown: {
    title: 'Unknown outcomes are never blindly retried',
    body: 'A command whose outcome is unknown (timeout after send, connection reset) is not re-sent. Reconciliation asks the registry what happened and records success, rejection or an item for manual review; what the customer is charged and what we publish follows that answer.',
    link: 'Open reconciliation',
  },
  tx: {
    started: 'Started',
    registry: 'Registry',
    command: 'Command',
    object: 'Object',
    state: 'State',
    result: 'Result',
    trids: 'clTRID / svTRID',
    classLatency: 'Class / latency',
    corr: 'corr',
    workflow: 'workflow',
    attempt: 'attempt #{{n}}',
    open: 'Open command {{id}}',
  },
  classTable: {
    empty: 'No traffic classes are set, so a single interactive session is used.',
    class: 'Traffic class',
    sessions: 'Sessions',
    rps: 'Commands/s',
    burst: 'Burst',
  },
  registries: {
    title: 'Registries',
    subtitle: 'Every registry the registrar sends domain commands to, and what is waiting for attention at each one.',
    loadError: 'Could not load registries',
    empty: 'No registries configured',
    totals: 'All registries',
    openCommands: 'Open commands',
    openCommandsHint: 'In flight or unresolved',
    unknown: 'Unknown outcomes',
    unknownHint: 'Awaiting reconciliation',
    drift: 'Drift issues',
    driftHint: 'Open reconciliation items',
    poll: 'Unprocessed poll',
    pollHint: 'Poll messages not processed',
    viewList: 'View list',
    healthy: 'OK',
    attention: 'Attention',
    open: 'Open registry',
    adapter: 'Connection type',
    tlds: 'Zones',
    environments: 'Connection',
    notConfigured: 'Not configured',
    inactive: 'inactive',
    capabilities: 'What this registry allows',
    capabilitiesVersion: 'v{{version}}, in force since {{date}}',
    limits: 'Registration up to {{reg}} years, renewal up to {{renew}} years.',
    noCapabilities: 'Nothing is recorded about what this registry allows, so commands that depend on it are refused.',
    supported: 'Supported',
    notSupported: 'Not supported',
    cap: {
      supports_epp: 'EPP',
      supports_contact_objects: 'Contact objects',
      supports_host_objects: 'Host objects',
      supports_dnssec: 'DNSSEC',
      supports_rgp: 'Redemption period',
      supports_fee_extension: 'Price in commands',
      supports_change_poll: 'Change poll',
      supports_idn: 'IDN',
      supports_premium: 'Premium names',
      supports_transfer: 'Transfer',
      supports_restore: 'Restore',
      requires_local_presence: 'Local presence required',
      requires_documents: 'Documents required',
    },
  },
  registryPage: {
    tab: { overview: 'Overview', connection: 'Connection & certificates', sessions: 'EPP sessions', transactions: 'Transactions', poll: 'Poll', reconciliation: 'Reconciliation', ote: 'OT&E' },
    subtitle: 'What this registry connects to, what it allows, the zones it runs and the work waiting on it.',
    loadError: 'Could not load this registry',
    notFound: 'No registry with this code',
    overview: 'Registry',
    id: 'ID',
    code: 'Code',
    status: 'Status',
    created: 'Added',
    updated: 'Last changed',
    environments: 'Connection settings',
    noEnvironments: 'No connection is set up, so no command can reach this registry.',
    endpoint: 'Address',
    inUse: 'In use',
    login: 'Login',
    certificate: 'Client certificate',
    secretsNote: 'Only whether a password or a private key is stored is shown here — never the value itself.',
    passwordStored: 'Password stored',
    passwordMissing: 'No password stored',
    keyStored: 'Private key stored',
    keyMissing: 'No private key stored',
    noLogin: 'No login recorded',
    noCertificate: 'No certificate recorded',
    version: 'version {{n}}',
    validity: '{{from}} → {{to}}',
    expiresSoon: '{{count}} days left',
    expired: 'Expired',
    zones: 'Zones run by this registry',
    noZones: 'No zone is assigned to this registry',
    recentCommands: 'Latest commands',
    noCommands: 'No command has been sent to this registry yet',
    openIssues: 'Open drift',
    noOpenIssues: 'No open drift for this registry',
    pollQueue: 'Poll queue',
    noPollMessages: 'No poll message received from this registry',
    openEpp: 'Open EPP page',
  },
  catalog: {
    title: 'TLD catalogue',
    subtitle: 'Every TLD in the world (IANA) and registrable second-level zones (Public Suffix List). Enable a TLD to sell it, then set its channel, registry cost and selling prices.',
    count: '{{total}} matching zones (catalogue of {{size}}, version {{version}})',
    search: 'Find a zone',
    zone: 'Zone',
    class: 'Class',
    ianaType: 'Type (IANA)',
    secondLevel: 'second level of .{{parent}}',
    lookup: 'RDAP / WHOIS',
    state: 'State',
    selling: 'Selling · {{registry}}',
    suspended: 'Switched off',
    off: 'Not enabled',
    filterEnabled: 'Selling',
    filterDisabled: 'Not enabled',
    empty: 'No zone matches.',
    enable: 'Enable TLD',
    enableTitle: 'Enable .{{zone}}',
    enableIntro: 'Class {{cls}}, dossier type {{dossier}} from the catalogue.',
    registry: 'Registry / connection the zone belongs to',
    registryHint: 'Where the zone belongs (not used for prices).',
    choose: 'Choose…',
    copyFrom: 'Initial set-up',
    copyFromHint: 'Copy the lifecycle, transfer / restore terms and domain services of a TLD you sell, or use the class defaults.',
    classDefaults: 'Defaults of class {{cls}}',
    copyPrices: 'Also copy its registry cost and selling prices (new versions, from now)',
    enabled: '.{{zone}} enabled.',
    reactivated: '.{{zone}} enabled again.',
    already: '.{{zone}} is already sold.',
    next: 'Next: registration channel, registry cost and selling prices.',
    disable: 'Switch off',
    disableTitle: 'Switch off .{{zone}}',
    disableHint: 'The zone stops being sold (no quotes, no new registrations); nothing is deleted and domains already held are unchanged.',
    disabled: 'Switched off.',
    outsideTitle: 'Zones enabled outside the catalogue',
    outsideHint: 'Enabled before the catalogue existed or by test data (e.g. .example, .test). They cannot be enabled again here, only taken off sale.',
    stopSelling: 'Stop selling',
    stopSellingTitle: 'Stop selling .{{zone}}',
    stopped: 'No longer sold.',
    previous: 'Previous',
    nextPage: 'Next',
  },
  tlds: {
    title: 'Zones',
    subtitle: 'The zones customers can register in, with the rules that apply and how many names the registrar holds.',
    loadError: 'Could not load zones',
    empty: 'No zone matches',
    zone: 'Zone',
    registry: 'Registry',
    class: 'Class',
    dossier: 'Dossier type',
    icann: 'ICANN',
    governed: 'Governed',
    rdapWhois: 'RDAP / WHOIS',
    metricGroup: 'Reporting group',
    status: 'Status',
    managed: 'Domains held',
    on: 'on',
    off: 'off',
    open: 'Open zone {{zone}}',
  },
  problem: {
    reason: {
      REGISTRY_NOT_CONFIGURED: "This TLD's registry connection cannot be used yet",
      REGISTRY_AUTH_FAILED: 'The registrar refused the login',
      REGISTRY_ERROR: 'The registrar answered with an error',
      REGISTRY_TIMEOUT: 'The registrar did not answer in time',
      TLD_NOT_OFFERED_BY_REGISTRY: 'The registrar does not offer this extension',
      other: 'The registrar could not be asked',
    },
    missing: {
      CONNECTION_UNKNOWN: 'The connection no longer exists',
      CONNECTION_INACTIVE: 'The connection is suspended',
      CONNECTION_DISABLED: 'The connection settings are not enabled',
      ENDPOINT_MISSING: 'No server address',
      SETTINGS_INVALID: 'Invalid settings',
      CREDENTIAL_MISSING: 'No login details',
      CERTIFICATE_MISSING: 'No client certificate',
      CERTIFICATE_INVALID: 'The certificate cannot be used',
      CAPABILITIES_MISSING: "The registry's rules are not recorded",
      ADAPTER_UNAVAILABLE: 'This connection is not supported',
      SECRET_UNAVAILABLE: 'The stored secret cannot be read',
      WRONG_CREDENTIALS: 'Wrong API username / secret',
      IP_NOT_ALLOWED: "This server's IP is not allowed",
      CREDENTIALS_REJECTED: 'Refused: wrong login details or IP not allowed',
      API_REJECTED: 'Signed in, but the read call was refused',
      ADDRESS_NOT_ALLOWED: "The registrar's address may not be called from here",
      API_TIMEOUT: 'The registrar did not answer in time',
      API_UNREACHABLE: 'Could not connect to the registrar',
      API_UNEXPECTED_RESPONSE: 'The answer could not be read',
      CONFIGURATION_INVALID: 'Invalid settings',
    },
    provider: "Registrar's answer: {{code}} {{message}}",
    openConnection: 'Open the {{name}} connection',
    openTld: 'Open the TLD page',
    hintAuth: "Check the API username / secret of the right environment (OT&E or production) and add this server's IP address to the allowed IP list in the registrar's portal.",
  },
  publication: {
    US: 'Published by Tino',
    UPSTREAM: 'Published by {{name}}',
    REGISTRY: 'Published by the registry',
    PARTIAL: 'Partly on',
    OFF: 'Not on',
    hint: {
      US: "RDAP / WHOIS for this zone's domains is answered by Tino's servers.",
      UPSTREAM: 'Domains registered through {{name}}: {{name}} is their registrar, so {{name}} (and the registry) publish RDAP / WHOIS, not Tino.',
      REGISTRY: "The registry publishes this zone's registration data.",
      PARTIAL: 'Tino publishes only one of RDAP and WHOIS for this zone.',
      OFF: 'Tino does not publish RDAP / WHOIS for this zone yet.',
    },
  },
  readiness: {
    costNotChannel: 'This cost was set before the registration channel moved to {{name}}: it is the previous channel’s price. Enter {{name}}’s cost.',
    title: 'Ready to sell',
    ready: 'Ready to sell',
    notReady: 'Not sellable yet',
    missingList: 'Missing: {{list}}',
    warnList: 'To review: {{list}}',
    intro: 'What .{{zone}} needs before customers can register it. Each unmet item has a fix or a link to where it is fixed.',
    state: { OK: 'Done', MISSING: 'Missing', WARN: 'To review', UNKNOWN: 'Not checked' },
    item: {
      CONNECTION: 'Connection usable',
      LOGIN_TEST: 'Login to the registrar tested',
      REGISTRY_COST: 'Registry cost (register, renew, restore, transfer)',
      SALE_PRICE: 'Retail selling price (default tier)',
      FX_RATE: 'Exchange rate',
      ADDONS: 'Fees & add-ons',
      PUBLICATION: 'RDAP / WHOIS',
      AVAILABILITY: 'Availability check',
    },
    short: {
      CONNECTION: 'connection',
      LOGIN_TEST: 'login test',
      REGISTRY_COST: 'registry cost',
      SALE_PRICE: 'selling price',
      FX_RATE: 'exchange rate',
      ADDONS: 'add-ons',
      PUBLICATION: 'RDAP / WHOIS',
      AVAILABILITY: 'name check',
    },
    connectionOk: 'Through {{name}} ({{source}}).',
    source: { CONFIGURED: "the TLD's own channel", DEFAULT: 'by default' },
    loginNever: 'The login of the {{name}} connection has not been tested.',
    loginOk: 'Last test succeeded {{when}}.',
    loginFailed: 'Last test failed {{when}}.',
    costsMissingOps: 'No registry cost for: {{ops}}.',
    noCost: 'No registration cost.',
    noBook: 'No retail price book for the default tier.',
    saleMissing: 'Price book {{books}} has no registration price for this TLD.',
    renewMissing: 'No renewal price.',
    saleOk: 'Priced in {{books}}.',
    fxMissing: 'No exchange rate {{pairs}}.',
    addonsNotDecided: 'No add-on chosen yet. Defaults for this kind of TLD: {{list}}.',
    addonsUnpriced: 'Mandatory add-ons without a price: {{list}}.',
    addonsIncluded: "{{list}} is included in the upstream registrar's price, so it is not charged through this channel; switch it off so the price list matches the quote.",
    addonsOk: '{{count}} add-on(s) offered.',
    addonsNone: 'This kind of TLD has no default add-on.',
    availabilityHint: 'Ask the registrar about a real name to be sure the connection, the login and the TLD all work.',
    fix: {
      OPEN_CONNECTION: 'Open connection',
      OPEN_PRICING: 'Open prices & services',
      SET_SALE_PRICE: 'Set selling price',
      APPLY_DEFAULT_ADDONS: 'Turn on the default services',
      ENABLE_PUBLICATION: 'Turn on RDAP / WHOIS',
      TEST_CHECK: 'Try checking a name',
    },
    reasonDefaults: 'Default services turned on from the TLD checklist',
    reasonPublication: 'RDAP / WHOIS turned on from the TLD checklist',
    reasonSale: 'Selling price set from the TLD checklist',
    saleTitle: 'Selling price of .{{zone}}',
    margin: 'Margin % over cost',
    fixed: 'Fixed price per year',
    value: 'Value',
    books: 'Applies to: {{books}}',
    testName: 'Name to check',
    testPlaceholder: 'empty: a random name',
    test: 'Check',
    testAvailable: '{{name}} is available: the connection, the login and the TLD all work.',
    testTaken: '{{name}} is registered already (the registrar answered normally).',
  },
  setup: {
    button: 'Add TLD',
    title: 'Add a TLD to sell',
    description: 'Choose the connection, the TLD, the registry cost and the selling price. Add-ons and RDAP / WHOIS follow the kind of TLD.',
    connection: 'Registration connection',
    connectionHint: "This connection takes the TLD's new registrations and transfers.",
    choose: 'Choose…',
    zone: 'TLD',
    zoneSearch: 'Search extensions',
    fromConnection: 'The extensions {{name}} offers.',
    fromCatalog: "The TLD catalogue (the connection's own list is not available).",
    selling: 'selling',
    noZone: 'No matching TLD.',
    cost: 'Registry cost per year',
    costHint: 'One cost for register, renew, restore and transfer. Change single operations if they differ.',
    currency: 'Currency',
    perOperation: 'Edit each operation',
    op: { REGISTER: 'Register', RENEW: 'Renew', RESTORE: 'Restore', TRANSFER: 'Transfer' },
    sale: 'Selling price',
    saleHint: 'Applied to the retail price books of the default tier: {{books}}.',
    noBooks: 'No retail price book for the default tier: create one in Products & prices first.',
    margin: 'Margin % over cost',
    fixed: 'Fixed price per year ({{currency}})',
    addons: 'Add-ons (turned on automatically)',
    addonsNone: 'This kind of TLD has no default add-on.',
    addonCost: '{{name}} cost per year',
    mode: { MANDATORY: 'mandatory', DEFAULT: 'on by default', OPTIONAL: 'optional' },
    leftOut: "The ICANN fee is not added: {{name}}'s price includes it.",
    publication: 'RDAP / WHOIS',
    summary: '.{{zone}} through {{connection}}; cost {{cost}} {{currency}}; price {{sale}}.',
    summaryMargin: '{{value}}% margin',
    summaryFixed: '{{value}} {{currency}} per year',
    create: 'Create TLD',
    created: '.{{zone}} added.',
    openTld: 'Open .{{zone}}',
  },
  tldPage: {
    tab: { info: 'Details', channels: 'Registration channel', lifecycle: 'Lifecycle & transfer / restore', pricing: 'Prices & services', services: 'Services', policies: 'Policies' },
    subtitle: 'The rules that apply to this zone and the names the registrar holds in it.',
    loadError: 'Could not load this zone',
    notFound: 'No zone with this reference',
    rules: 'Zone rules',
    id: 'ID',
    prices: 'Open price list',
    domains: 'Domains held',
    channel: 'Registration channel (production)',
    costs: 'Registry cost in force',
    noCost: 'No registry cost',
    op: { REGISTER: 'Register', RENEW: 'Renew' },
    addons: 'Add-ons offered',
    addonsMandatory: '{{count}} mandatory',
    addonsMissing: '{{count}} mandatory add-on(s) without a price',
    byState: 'Domains by state',
    noByState: 'No domain in this zone yet',
    recent: 'Newest registrations',
    noRecent: 'No domain has been registered in this zone yet',
    domain: 'Domain',
    state: 'State',
    registered: 'Registered at registry',
    expires: 'Expires at registry',
    added: 'Added here',
  },
  epp: {
    title: 'EPP connections',
    subtitle: 'How the registrar reaches each registry: the address it dials, how many commands it may send at once, and whether the login and certificate are in place.',
    secretsTitle: 'Passwords and private keys are never shown',
    secrets:
      'This page only says whether a password or a private key is stored. Secret locations and the EPP login name are shown to staff who are allowed to manage registry logins; nobody sees the values. Nothing on this page can be changed here.',
    loadError: 'Could not load the EPP connections',
    empty: 'No registry connection set up',
    active: 'Active',
    inactive: 'Inactive',
    noHost: 'no address',
    settingsError: 'The settings of this connection cannot be read, so the registrar cannot open a connection to it. The settings need to be corrected.',
    connection: 'Connection',
    tls: 'TLS',
    tlsOn: 'on',
    sni: 'SNI',
    plainTcp: 'plain TCP',
    privateCa: 'Own certificate authority',
    systemRoots: 'Public authorities',
    connectTimeout: 'Connect timeout',
    commandTimeout: 'Command timeout',
    acquireTimeout: 'Wait for a free session',
    breaker: 'Stop after repeated failures',
    breakerValue: 'after {{n}} failures, for {{duration}}',
    maxCommands: 'Commands per session',
    maxIdle: 'Idle before reconnect',
    maxLifetime: 'Session lifetime',
    pools: 'Session limits by traffic class',
    credentials: 'Login',
    noCredentials: 'No login is stored, so the registrar cannot sign in to this registry.',
    noActiveCredential: 'No login version is in use.',
    version: 'Version',
    status: 'Status',
    clientId: 'Login name',
    hidden: 'hidden',
    password: 'Password',
    activated: 'In use since',
    retired: 'Retired',
    certificates: 'Client certificates',
    noCertificates: 'No client certificate recorded.',
    noActiveCertificate: 'No certificate version is in use.',
    valid: 'Valid',
    privateKey: 'Private key',
    expired: 'Expired',
    daysLeft_one: '{{count}} day left',
    daysLeft_other: '{{count}} days left',
    configured: 'Stored',
    notConfigured: 'Not stored',
    openRegistry: 'Open registry',
  },
  sessions: {
    title: 'EPP sessions',
    subtitle: 'How many EPP sessions each registry allows per traffic class, the commands sent in the last hours, and any session commands.',
    limitTitle: 'Live sessions cannot be shown here',
    limit:
      'Open connections and signed-in sessions only exist while the system is running and are not kept, so they cannot be shown here. What is kept is shown below: the session limits per traffic class, the commands of the last hours, and any session commands.',
    limitLink: 'For live connectivity, latency and error rates see Registry health',
    loadError: 'Could not load session data',
    pools: 'Session limits per connection',
    noPools: 'No registry connection set up',
    recycle: 'Sessions are renewed after {{commands}} commands, {{idle}} idle or {{lifetime}} in use. Waiting for a free session gives up after {{acquire}}.',
    noRecycle: 'Sessions are kept open with no command or time limit. Waiting for a free session gives up after {{acquire}}.',
    traffic: 'Commands in the last {{hours}} hours by traffic class',
    noTraffic: 'No command was sent in this period',
    registry: 'Registry',
    class: 'Traffic class',
    total: 'Total',
    success: 'Success',
    rejected: 'Rejected',
    unknown: 'Unknown outcome',
    inFlight: 'In flight',
    maxLatency: 'Slowest',
    last: 'Last command',
    sessionCommands: 'Session commands (login / logout / hello)',
    noSessionTx: 'No session command recorded',
    noSessionTxBody: 'Signing in, signing out and connection checks are not recorded as registry commands today.',
  },
  transactions: {
    title: 'Registry commands',
    subtitle: 'Every command the registrar sent to a registry, with its clTRID / svTRID and outcome, newest first.',
    loadError: 'Could not load registry commands',
    empty: 'No command matches',
    onlyUnknown: 'Unknown outcomes only',
    listTitle: 'Commands',
    count: '{{count}} on this page',
  },
  poll: {
    title: 'Poll messages',
    subtitle: 'Messages the registries sent to the registrar — transfers, renewals, status changes — and whether each one has been handled.',
    loadError: 'Could not load poll messages',
    empty: 'No poll message matches',
    queue: 'Queue per registry',
    registry: 'Registry',
    receivedCount: 'Received',
    unprocessed: 'Not processed',
    unacked: 'Not acknowledged',
    errors: 'Errors',
    oldest: 'Oldest not processed',
    lastReceived: 'Last received',
    messages: 'Messages',
    received: 'Received',
    messageId: 'Message number',
    message: 'Message',
    object: 'Object',
    queuedAt: 'Raised by registry',
    acked: 'Acknowledged',
    processed: 'Processed',
    no: 'Not yet',
    error: 'Error',
    pending: 'Pending',
    open: 'Open message',
    openOne: 'Open message {{id}}',
    openRegistry: 'Open registry',
  },
  pollMessage: {
    title: 'Poll message',
    subtitle: 'One message a registry sent to the registrar, and what it refers to.',
    loadError: 'Could not load this message',
    notFound: 'No poll message with this reference',
    details: 'Message',
    id: 'ID',
    content: 'Message content',
    contentNote: 'Values that could reveal a secret are removed before this is shown, and opening it is recorded.',
    contentWithheld: 'The stored content is not shown to you. Ask a colleague who is allowed to open registry payloads.',
    linked: 'What this message is about',
    objectName: 'Object at the registry',
    processingError: 'Why it could not be handled',
    handled: 'Handled',
    notHandled: 'Not handled yet',
  },
  reconciliation: {
    title: 'Reconciliation',
    subtitle: 'Commands whose outcome the registry has not confirmed, and differences between our records and the registry.',
    loadError: 'Could not load the reconciliation queue',
    unknownTitle: 'Commands with unknown outcome',
    unknownDesc: '{{count}} commands, oldest first',
    noUnknown: 'No unknown outcomes',
    issuesTitle: 'Differences waiting to be resolved',
    issuesDesc: '{{count}} items',
    anyOpen: 'Open (any open state)',
    noIssues: 'No item matches',
    detected: 'Found',
    registry: 'Registry',
    domain: 'Domain',
    type: 'Type',
    severity: 'Severity',
    state: 'State',
    transaction: 'Command',
    command: 'command',
    resolution: 'Resolution',
    local: 'Our records',
    remote: 'Registry',
    withheld: '{{label}}: not shown',
    noValue: '{{label}}: none',
    unknownActor: 'unknown',
    open: 'Open item',
    openOne: 'Open {{type}} item',
  },
  issue: {
    subtitle: 'What we hold, what the registry holds, and what has been done about it.',
    loadError: 'Could not load this item',
    notFound: 'No reconciliation item with this reference',
    summary: 'Item',
    id: 'ID',
    compare: 'Our records against the registry',
    compareNote: 'Both sides are shown with personal data and secrets removed.',
    command: 'Command it came from',
    noCommand: 'This difference was not traced back to a command',
    openCommand: 'Open the command',
    started: 'Started',
    completed: 'Completed',
    result: 'Result',
    errorClass: 'Failure',
    trids: 'clTRID / svTRID',
    resolvedAt: 'Resolved',
    resolvedBy: 'Resolved by',
    history: 'Other differences on this domain',
    noHistory: 'No other difference recorded for this domain',
    commands: 'Recent commands for this domain',
    noCommands: 'No command recorded for this domain',
  },
  hub: {
    links: 'Related pages',
    link: { pricing: 'Price books', promotions: 'Promotions', services: 'Add-ons & fees', quote: 'Price calculator', catalog: 'TLD catalogue', channels: 'Registrar & routing' },
    book: 'Price book',
    bookNote: 'Prices of the {{tier}} tier · {{currency}} · one year, before tax',
    bookNoteOther: '{{currency}} · one year, before tax',
    noBook: 'No price book in use yet: create one under Price books.',
    pricesHidden: 'Prices are not shown: viewing price books needs the pricing permission.',
    search: 'Search',
    searchPlaceholder: 'TLD or connection',
    group: 'TLD group',
    col: { tld: 'TLD', connection: 'Registration connection', register: 'Register', renew: 'Renew', transfer: 'Transfer in', restore: 'Restore', effective: 'In force', ready: 'Ready to sell', state: 'Sales state' },
    catalog: {
      size: 'Zones in the catalogue',
      sizeHint: 'Catalogue version {{version}}',
      selling: 'On sale',
      sellingHint: 'Switched on here or with “Add TLD”',
      sellingLink: 'Open TLDs & prices',
      matches: 'Matching the filter',
      matchesHint: 'Search, group and state above the list',
    },
    step: { connection: 'Connection', zone: 'TLD', cost: 'Cost', sale: 'Price', addons: 'Services', create: 'Create' },
    readinessProgress: 'Checks passed',
    missingShort: 'Missing',
    effectiveNow: 'In force',
    effectiveFrom: 'New price from {{date}}',
    noCost: 'No cost',
    noRule: 'No price',
    notOffered: 'Not sold',
    free: 'Free',
    shown: '{{shown}} of {{total}} TLDs',
    selectRow: 'Show the profile of .{{zone}}',
    model: {
      title: 'How the selling price is made',
      description: 'The price a customer pays is fixed in the quote and the order line; a later price book change does not change it.',
      cost: 'Cost',
      costHint: 'Registry / partner registrar',
      margin: 'Margin',
      marginHint: 'Per TLD or for the whole book',
      sale: 'Selling price',
      saleHint: 'Tier book / customer price',
      quote: 'Fixed price',
      quoteHint: 'On the quote and the order',
      premium: 'Premium names',
      premiumHint: 'The registry’s price replaces the cost; only a margin applies and the quote expires sooner',
      promotions: 'Promotions',
      promotionsHint: 'Discount codes per TLD and operation',
      fx: 'Exchange rate',
      fxHint: 'Foreign-currency costs at the rate in force',
      tax: 'Tax',
      taxHint: 'VAT per line',
      addons: 'Fees & add-ons',
      addonsHint: 'Mandatory fees and optional services per TLD',
    },
    profile: {
      title: 'TLD profile · .{{zone}}',
      description: 'Rules and registration route of the selected TLD',
      none: 'Pick a TLD in the list.',
      open: 'Open the TLD page',
      prices: 'Prices & services',
      channel: 'Registration route',
      costRegister: 'Registration cost',
    },
    versions: {
      title: 'Price versions · {{book}}',
      description: 'Selling prices of .{{zone}} in this book over time',
      op: '{{op}} · version {{version}}',
      shared: '{{op}} · book-wide price, version {{version}}',
      active: 'In force',
      scheduled: 'Scheduled',
      ended: 'Replaced',
      window: 'From {{from}}',
      windowTo: 'From {{from}} to {{to}}',
      empty: 'No selling price for .{{zone}} in this book yet.',
      approval: 'Selling prices take effect at their start time without a second approver; every change is recorded in the audit log.',
    },
  },
} as const;
